import React, { useState, useEffect } from "react";
import api from "services/api";
import PageHeadline from "elements/PageHeadline";
import { __ } from "services/Translator";
import { Accordion, Modal } from "react-bootstrap";
import ModalCloseButton from "components/ModalCloseButton";

// Document verification tab. The personal-details form that used to live here as
// "Level 1" now lives in Account Details (progressive-profiling, write-once), so
// this tab is purely the identity + address document uploads. No "Level" wording.
export default function Verify(props) {
  const [poib, setPoib] = useState();
  const [poif, setPoif] = useState();
  const [poa, setPoa] = useState();

  const [identityVerified, setIdentityVerified] = useState(false);
  const [identitySubmitted, setIdentitySubmitted] = useState(false);
  const [addressVerified, setAddressVerified] = useState(false);
  const [addressSubmitted, setAddressSubmitted] = useState(false);

  const getIdUploads = async () => {
    var response = await api.get("/id-uploads");
    if (response.status === 200 && response.data) {
      setPoib(response.data.poib);
      setPoif(response.data.poif);
      setPoa(response.data.poa);

      if (response.data.poif) {
        if (response.data.poif.approved == "approved") {
          setIdentityVerified(true);
        } else if (response.data.poif.approved == "submitted") {
          setIdentitySubmitted(true);
        }
      }

      if (response.data.poa) {
        if (response.data.poa.approved == "approved") {
          setAddressVerified(true);
        } else if (response.data.poa.approved == "submitted") {
          setAddressSubmitted(true);
        }
      }
    }
  };

  useEffect(() => {
    getIdUploads();
  }, []);

  // ---- Proof of identity ----
  // The document type decides how many sides are required: a passport is a single
  // photo page (front only), an ID card or driver's licence needs front + back.
  const [idDocType, setIdDocType] = useState("id_card"); // "id_card" | "passport"
  const [identityFrontFileName, setIdentityFrontFileName] = useState();
  const [identityFrontFile, setIdentityFrontFile] = useState();
  const pickIdentityFront = (event) => {
    if (!event.target.files?.[0]) return;
    setIdentityFrontFileName(event.target.files[0].name);
    setIdentityFrontFile(event.target.files[0]);
  };

  const [identityBackFileName, setIdentityBackFileName] = useState();
  const [identityBackFile, setIdentityBackFile] = useState();
  const pickIdentityBack = (event) => {
    if (!event.target.files?.[0]) return;
    setIdentityBackFileName(event.target.files[0].name);
    setIdentityBackFile(event.target.files[0]);
  };

  const [uploadIFRes, setUploadIFRes] = useState(false);
  const uploadIdentityFrontFile = async () => {
    const formData = new FormData();
    formData.append("type", "poif");
    formData.append("file", identityFrontFile);
    var response = await api.post("/id-uploads", formData);
    if (response.status == 200) {
      setUploadIFRes(true);
      setIdentityFrontFile("");
      setIdentityFrontFileName("");
    } else {
      props.showAlert(__("Error!!"), response.data.message, true);
      setUploadIFRes(false);
    }
  };

  const [uploadIBRes, setUploadIBRes] = useState(false);
  const uploadIdentityBackFile = async () => {
    const formData = new FormData();
    formData.append("type", "poib");
    formData.append("file", identityBackFile);
    var response = await api.post("/id-uploads", formData);
    if (response.status == 200) {
      setUploadIBRes(true);
      setIdentityBackFileName("");
      setIdentityBackFile("");
    } else {
      props.showAlert(__("Error!!"), response.data.message, true);
      setUploadIBRes(false);
    }
  };

  const uploadProofOfIdentity = () => {
    // The front (passport photo page, or ID/licence front) is always required.
    // The back is required only for an ID card or driver's licence. Guard catches
    // both "not picked" and the post-upload "" reset so a second click cannot
    // POST an empty file.
    if (!identityFrontFile) {
      const missing =
        idDocType === "passport"
          ? __("Please upload the photo page of your passport.")
          : __("Please upload the front of your ID card or licence.");
      props.showAlert(__("Error!!"), missing, true);
      return;
    }
    if (idDocType === "id_card" && !identityBackFile) {
      props.showAlert(__("Error!!"), __("The back of your ID card or licence is also required."), true);
      return;
    }
    uploadIdentityFrontFile();
    if (idDocType === "id_card" && identityBackFile) {
      uploadIdentityBackFile();
    }
  };

  useEffect(() => {
    // Confirmation is keyed on the mandatory front upload; the optional back is
    // best-effort and does not gate it.
    if (uploadIFRes) {
      props.showAlert(__("Success!!"), __("Proof Of Identity Successfully Uploaded!"));
      setUploadIBRes(false);
      setUploadIFRes(false);
      getIdUploads();
    }
  }, [uploadIFRes]);

  // ---- Proof of address ----
  const [addressFileName, setAddressFileName] = useState();
  const [addressFile, setAddressFile] = useState();
  const pickAddressFile = (event) => {
    if (!event.target.files?.[0]) return;
    setAddressFileName(event.target.files[0].name);
    setAddressFile(event.target.files[0]);
  };

  const [uploadAFRes, setUploadAFRes] = useState(false);
  const uploadAddressFile = async () => {
    const formData = new FormData();
    formData.append("type", "poa");
    formData.append("file", addressFile);
    var response = await api.post("/id-uploads", formData);
    if (response.status == 200) {
      setUploadAFRes(true);
      setAddressFile("");
      setAddressFileName("");
    } else {
      props.showAlert(__("Error!!"), response.data.message, true);
      setUploadAFRes(false);
    }
  };

  const uploadProofOfAddress = () => {
    if (!addressFile) {
      props.showAlert(null, __("Proof of address is required."), true);
      return;
    }
    uploadAddressFile();
    window.scrollTo(0, 0);
  };

  useEffect(() => {
    if (uploadAFRes) {
      props.showAlert(__("Success"), __("Proof Of Address Successfully Uploaded"));
      setUploadAFRes(false);
      getIdUploads();
    }
  }, [uploadAFRes]);

  // ---- Document preview ----
  const [perviewImageModal, setPerviewImageModal] = useState(false);
  const [perviewImage, setPerviewImage] = useState([]);
  const showBigImage = (img) => {
    setPerviewImage(img);
    setPerviewImageModal(true);
  };
  const closePerviewImageModal = () => {
    setPerviewImageModal(false);
  };

  const statusIcon = (verified, submitted) =>
    verified ? (
      <i className="fas fa-check"></i>
    ) : (
      <i className={"fas fa-exclamation " + (submitted ? "submitted" : "")}></i>
    );

  return (
    <>
      <PageHeadline page_name={__("Verify your account")} />
      <div className="access_control_area verify_area">
        <div className="account-lock-note">
          <i className="fal fa-info-circle" />
          <span>
            {__(
              "Upload clear photos or scans of your documents. Accepted formats: JPEG, PNG or PDF, up to 2MB per file."
            )}
          </span>
        </div>

        <Accordion className="faq-modern" defaultActiveKey="0">
          <Accordion.Item className="chimp-accordion faq-modern__category" eventKey="0">
            <Accordion.Header className="faq__question">
              {__("Proof of identity")} &nbsp; {statusIcon(identityVerified, identitySubmitted)}
            </Accordion.Header>
            <Accordion.Body className="faq__answer myaccount_input_area">
              <div className="row">
                <div className="col-md-6">
                  <div className="form_area">
                    <div className="input_item">
                      <div className="name">{__("Which document are you uploading?")}</div>
                      <div className="doc_type_choice">
                        <label className={"doc_type_option " + (idDocType === "id_card" ? "active" : "")}>
                          <input
                            type="radio"
                            name="id_doc_type"
                            checked={idDocType === "id_card"}
                            onChange={() => setIdDocType("id_card")}
                          />
                          {__("ID card or driver's licence")}
                        </label>
                        <label className={"doc_type_option " + (idDocType === "passport" ? "active" : "")}>
                          <input
                            type="radio"
                            name="id_doc_type"
                            checked={idDocType === "passport"}
                            onChange={() => setIdDocType("passport")}
                          />
                          {__("Passport")}
                        </label>
                      </div>
                    </div>

                    <div className="input_item">
                      <div className="name">
                        {idDocType === "passport"
                          ? __(
                              "Upload the photo page of your passport. It must lie flat with all four corners in frame and all details clearly readable."
                            )
                          : __(
                              "Upload the front and back of your ID card or driver's licence. Each side must lie flat with all four corners in frame and all details clearly readable."
                            )}
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-6">
                        <div className="input_item">
                          <div className="name">
                            {idDocType === "passport" ? __("Passport photo page") : __("Front")}
                            <span className="requried_icon"> *</span>
                          </div>
                          <div className="form-control name_value overflow-hidden">{identityFrontFileName}</div>
                          <input
                            onChange={pickIdentityFront}
                            id="identity_front"
                            type="file"
                            hidden
                            accept=".jpg, .jpeg, .png, .pdf"
                            name="identity_front"
                            className="form-control input_box"
                          />
                          <label className="d-block" htmlFor="identity_front">
                            {!!identityFrontFileName || <i className="fal fa-file-upload in_input_icon"></i>}
                            {!!identityFrontFileName && (
                              <i className="fad fa-check-square in_input_icon upload_success"></i>
                            )}
                          </label>
                        </div>
                      </div>
                      {idDocType === "id_card" && (
                        <div className="col-md-6">
                          <div className="input_item">
                            <div className="name">
                              {__("Back")}
                              <span className="requried_icon"> *</span>
                            </div>
                            <div className="form-control name_value overflow-hidden">{identityBackFileName}</div>
                            <input
                              onChange={pickIdentityBack}
                              id="identity_back"
                              type="file"
                              hidden
                              accept=".jpg, .jpeg, .png, .pdf"
                              name="identity_back"
                              className="form-control name_value"
                            />
                            <label className="d-block" htmlFor="identity_back">
                              {!!identityBackFileName || <i className="fal fa-file-upload in_input_icon"></i>}
                              {!!identityBackFileName && (
                                <i className="fad fa-check-square in_input_icon upload_success"></i>
                              )}
                            </label>
                          </div>
                        </div>
                      )}
                    </div>

                    {!identityVerified && (
                      <div className="login_button">
                        <button onClick={uploadProofOfIdentity} className="commonBtn pTwoBtn ">
                          {__("Upload")}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                <div className="col-md-6">
                  <ul className="proof_view_area">
                    {poif && (
                      <li>
                        <img alt="" onClick={() => showBigImage(poif.api_photo_url)} src={poif.api_photo_url} />
                      </li>
                    )}
                    {poib && (
                      <li>
                        <img alt="" onClick={() => showBigImage(poib.api_photo_url)} src={poib.api_photo_url} />
                      </li>
                    )}
                  </ul>
                </div>
              </div>
            </Accordion.Body>
          </Accordion.Item>

          <Accordion.Item className="chimp-accordion faq-modern__category" eventKey="1">
            <Accordion.Header className="faq__question">
              {__("Proof of address")} &nbsp; {statusIcon(addressVerified, addressSubmitted)}
            </Accordion.Header>
            <Accordion.Body className="faq__answer myaccount_input_area">
              <div className="row">
                <div className="col-md-6">
                  <div className="form_area">
                    <div className="input_item">
                      <div className="name">
                        {__(
                          "Upload a document that shows all four corners, is no older than 3 months, and clearly shows your full name and full address, such as a utility bill."
                        )}
                      </div>
                    </div>
                    <div className="input_item">
                      <div className="name">
                        {__("Proof of address")}
                        <span className="requried_icon"> *</span>
                      </div>
                      <div className="form-control name_value overflow-hidden">{addressFileName}</div>
                      <input
                        onChange={pickAddressFile}
                        id="address_file"
                        type="file"
                        hidden
                        accept=".jpg, .jpeg, .png, .pdf"
                        name="address_file"
                        className="form-control name_value"
                      />
                      <label className="d-block" htmlFor="address_file">
                        {!!addressFileName || <i className="fal fa-file-upload in_input_icon"></i>}
                        {!!addressFileName && <i className="fad fa-check-square in_input_icon upload_success"></i>}
                      </label>
                    </div>

                    {!addressVerified && (
                      <div className="login_button">
                        <button onClick={uploadProofOfAddress} className="commonBtn pTwoBtn ">
                          {__("Upload")}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                <div className="col-md-6">
                  <ul className="proof_view_area">
                    {poa && (
                      <li>
                        <img alt="" onClick={() => showBigImage(poa.api_photo_url)} src={poa.api_photo_url} />
                      </li>
                    )}
                  </ul>
                </div>
              </div>
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>
      </div>

      <Modal show={perviewImageModal} onHide={closePerviewImageModal} size={"lg"}>
        <Modal.Body className="login_modal forgot_pass">
          <ModalCloseButton onClick={closePerviewImageModal} />
          <img alt="" style={{ width: "100%" }} src={perviewImage} />
        </Modal.Body>
      </Modal>
    </>
  );
}
